import { Section, Sub, Card, Note, Warn, Fields, Code, New, Shot } from '../../../components/docs/Blocks';

export default function RealmsServers() {
  return (
    <Section
      id="realms-servers"
      title={<>Realms &amp; Servers <New>New in 3.1</New></>}
      lead="Worlds that live somewhere other than this computer: the Realms you own, and the Bedrock dedicated servers you run. Download one, edit it like any other world, and send it back — or change its settings in place. Like Backups, this section opens without a world loaded."
    >
      <Sub id="realms-sign-in" title="Signing in">
        <Shot
          name="realms"
          alt="The Realms page before signing in, with Sign in with Microsoft and Sign in with a code instead"
        />
        <Card>
          <p>
            <strong>Realms</strong> starts signed out. <strong>Sign in with
            Microsoft</strong> opens your browser at Microsoft&rsquo;s own account
            picker; <strong>Sign in with a code instead</strong> gives you a code to
            enter on any device, for when the browser route will not work.
          </p>
          <Fields
            items={[
              ['What is sent where', 'Your sign-in goes to Microsoft and Xbox Live only, and the Realm requests go to the Realms service Minecraft itself uses. Nothing is sent to Bedrock Toolchest.'],
              ['What is kept', "The sign-in is kept on this computer, encrypted by the operating system — the user's protected storage on Windows, the keyring on Linux. Sign out removes it."],
              ['On Linux', 'Sign-in needs a keyring (libsecret, as GNOME and KDE provide). Without one, Toolchest refuses to sign in rather than keep the sign-in unencrypted.'],
            ]}
          />
          <Note>
            Microsoft does not publish the Realms service, so it can change without
            warning. If it does, the Realms page stops working until Toolchest is
            updated; nothing else in the program is affected.
          </Note>
        </Card>
      </Sub>

      <Sub id="realms-list" title="Your Realms">
        <Card>
          <p>
            Every Realm the signed-in account <em>owns</em>, open, closed or
            expired, with how many days are left and who is on it now. Only a
            Realm&rsquo;s owner can download and edit it, so Realms you have only
            been invited to are not listed.
          </p>
          <Fields
            items={[
              ['Plan', 'Whether the Realm is open, and how many players the plan allows. Renews or Ends gives the date.'],
              ['Members and Online now', 'How many players are on the Realm’s list, and how many are playing right now.'],
              ['Game Version, Size, Last backup', 'What the Realm is running, roughly how big its world is, and when Realms last backed it up.'],
              ['Region', 'Where the Realm is hosted.'],
              ['Add-ons', 'The packs the Realm’s world uses.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="realms-edit" title="Editing a Realm world">
        <Card>
          <p>
            Choose a <strong>World slot</strong> — each Realm holds three worlds, and
            only the active one is played — and press <strong>Download and
            edit</strong>. The copy opens like any other world, so every screen in
            the Level, Player and World Editors and Add-ons works on it. Save your
            changes as usual, then press <strong>Upload to Realm</strong>.
          </p>
          <Fields
            items={[
              ['Close the Realm while I edit', 'Closes the Realm until you upload or reopen it, so nobody plays in a world your upload is about to replace. Reopen without uploading puts it back if you change your mind.'],
              ['Inactive slots', "A slot that isn't active is downloaded from its newest Realm backup, which can be older than the world itself."],
              ['Upload to Realm', 'Replaces that slot’s world with your copy. Before it does, the world the Realm has now is downloaded and kept on the Backups page. Upload stays disabled until your changes are saved, and while the world needs add-ons that are missing.'],
              ['Your copy', 'A downloaded copy is kept until it has been uploaded, so you can come back to it later — Open it or Discard it from the Realm. Keep Realm copies after uploading, in Settings, keeps it afterwards too.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="realms-manage" title="Managing a Realm">
        <Card>
          <p>
            <strong>Manage this Realm</strong> covers what the Realms screen in
            Minecraft does, from the desktop.
          </p>
          <Fields
            items={[
              ['Realm', 'Change its name and description, open or close it, and choose the role new players join as.'],
              ['Worlds', 'Make another slot the active world, and change each world’s name and settings: whether players can hurt each other, command blocks, forcing the game mode, and whether players must accept the resource packs.'],
              ['Replace…', 'Puts a world from this computer in place of one of the Realm’s worlds. The world it replaces is saved to this computer first.'],
              ['Players', 'Invite a player by gamertag, set each player’s role — Visitor, Member or Operator — and remove or ban them. Removing is not a ban: a removed player can be invited again. A banned player cannot join, even by link, until they are unbanned.'],
              ['Invite link', 'Anyone with the link can join. New link makes a fresh one.'],
              ['Backups', 'The backups Realms keeps of the active world. Restore puts the world back to how it was then, after saving the world as it is now to this computer.'],
            ]}
          />
          <Note>
            Every change made here is backed up first and listed under Servers &amp;
            Realms on the{' '}
            <a href="#backups-remote" className="underline">Backups</a> page, so
            any of it can be put back.
          </Note>
        </Card>
      </Sub>

      <Sub id="servers" title="Dedicated servers">
        <Shot
          name="realms-servers"
          alt="The Servers page with its Add a server button"
        />
        <Card>
          <p>
            <strong>Servers</strong> connects to a Bedrock dedicated server over
            SFTP, with the details your host gives you. <strong>Add a
            server</strong> asks for a name, the host, port (22 unless your host
            says otherwise), username and password, and the folder on the host that
            holds <Code>server.properties</Code> — on a hosting panel&rsquo;s SFTP
            that is usually <Code>/</Code>. Leave World folder blank to use the
            world <Code>server.properties</Code> loads.
          </p>
          <Fields
            items={[
              ['The password', 'Kept on this computer, encrypted the same way as the Realms sign-in. On Linux without a keyring it is not saved at all, and you enter it each time.'],
              ['The server key', 'The first connection shows the server’s key fingerprint and asks you to trust it. If the key ever changes, Toolchest stops and asks again.'],
              ['Connect', 'Opens the world’s settings, game rules and add-ons straight from the server, without downloading the world. Send to server puts your changes back; Toolchest saves the server’s own copies first, and keeps anything the server saved in the meantime.'],
              ['Download the whole world', 'For everything else — players, the map, the rest of the World Editor. Edit and save as usual, then Upload to server. The world it replaces stays on the server as a previous copy.'],
              ['Server settings', 'The server’s own settings, written to server.properties: name, which world it loads, game mode, difficulty, how many players, cheats, the allow list, Xbox sign-in, new players’ permission, view and simulation distance, the idle kick and resource pack rules. They apply next time the server starts.'],
              ['On this computer or your network', 'Use the server’s local address. A server on Windows needs OpenSSH Server turned on for Toolchest to reach it.'],
            ]}
          />
          <Warn>
            Stop the server before sending, downloading or uploading — a running
            server can overwrite your changes, or be in the middle of writing the
            world you download. Toolchest asks you to confirm the server is stopped
            each time.
          </Warn>
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Hosting panels
        </h4>
        <Card>
          <p>
            If your server runs under a Pterodactyl panel (Kinetic and other hosts
            use it) or CubeCoders AMP, give Toolchest the panel&rsquo;s address and
            details and it asks the panel whether the server is stopped before
            changing anything. It only ever asks: it never starts or stops the
            server.
          </p>
          <Fields
            items={[
              ['Pterodactyl', 'The panel address, the server’s ID from the panel (not its name) and an API key.'],
              ['AMP', 'The panel address, the instance ID from the instance list (blank if the address is the instance’s own), and the panel username and password.'],
            ]}
          />
        </Card>
      </Sub>
    </Section>
  );
}
